import { useState } from 'preact/hooks'
import { ChevronDown, LogOut, Settings, Home } from 'lucide-preact'
import type { User as Auth0User } from '@auth0/auth0-spa-js'
import { PersonAvatar } from './Avatar'

interface UserMenuProps {
  user: Auth0User
  /**
   * The signed-in user's stored avatar, from their OrbitalX profile.
   *
   * Preferred over the Auth0 `picture` claim because that claim is whatever the identity provider
   * happened to hand Auth0, which for Microsoft is a Gravatar or generated initials image and never
   * the real photo. The stored avatar is the one the API resolves — including the Microsoft Graph
   * photo (ORB-634).
   *
   * Only `undefined` means "profile not loaded yet" and falls back to the claim. An explicit `null`
   * or empty string means the profile loaded and has no avatar, which is passed through so
   * PersonAvatar renders initials — falling back to the claim there would put the Auth0 placeholder
   * back on screen, which is the whole thing this prop exists to avoid.
   */
  profilePicUrl?: string | null
  onLogout: () => void
  onSettingsClick: () => void
  onBackToHome?: () => void
  backToHomeLabel?: string
}

export function UserMenu({ user, profilePicUrl, onLogout, onSettingsClick, onBackToHome, backToHomeLabel }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="relative">
      {/* User info button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        {/* Profile image */}
        <PersonAvatar
          fullName={user?.name || user?.email || 'User'}
          profilePicUrl={profilePicUrl === undefined ? user?.picture : profilePicUrl}
          size="lg"
        />

        {/* Name and email */}
        <div className="text-left hidden sm:block">
          <div className="text-sm font-medium text-slate-900 dark:text-white">
            {user?.name || user?.email}
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300">
            {user?.email}
          </div>
        </div>

        {/* Dropdown arrow */}
        <ChevronDown
          className={`h-4 w-4 text-slate-600 dark:text-slate-300 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-20">
            <div className="py-1">
              <button
                onClick={() => {
                  onSettingsClick()
                  setIsOpen(false)
                }}
                className="flex items-center gap-3 w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-colors duration-200"
              >
                <Settings className="h-4 w-4" />
                Account
              </button>

              {onBackToHome && (
                <button
                  onClick={() => {
                    onBackToHome()
                    setIsOpen(false)
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-colors duration-200"
                >
                  <Home className="h-4 w-4" />
                  {backToHomeLabel || 'Back to Home Company'}
                </button>
              )}

              <button
                onClick={() => {
                  onLogout()
                  setIsOpen(false)
                }}
                className="flex items-center gap-3 w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
